import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { AuthHttpService } from '@ib-core/services/auth-http.service';
import { LocalStorageService } from '@ib-core/services/local-storage.service';
import { NavigationService } from '@ib-core/services/navigation.service';
import { AuthFacadeService } from '@ib-core-services/auth-facade.service';
import { LOCAL_STORAGE_KEYS } from '@ib-enums/local-storage-keys.enum';
import { IB_ROUTES } from '@ib-enums/routes.enum';
import { environment } from '@ib-env/environment';
import { INVESTOR_SIGN_UP_STEPS } from 'credebt-shared';
import * as dayjs from 'dayjs';
import {
  catchError,
  combineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  startWith,
  Subject,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';

import { ISignUpDocListFormValue } from '../enums/sign-up-doc-list-form-value.intf';
import { SIGN_UP_FIELDS } from '../enums/sign-up-fields.enum';
import { ISignUpLocalStorage } from '../interfaces/sign-up-local-storage.intf';
import { ISignUpStep } from '../interfaces/sign-up-step.intf';
import { ISignUpWizardBtnsState } from '../interfaces/sign-up-wizard-btns-state.intf';
import { SignUpFormHelperService } from './sign-up-form-facade.service';
import { SignUpStateService } from './sign-up-state.service';

@Injectable()
export class SignUpFacadeService {
  private destroy$ = new Subject<void>();
  interval;
  // ---------- EVENTS

  public eventSendCodeToMobileSuccess$ = new Subject<void>();
  public eventSendCodeToMobileError$ = new Subject<HttpErrorResponse>();
  public handleExpireWithUnconfirmedCode$ = new Subject<void>();

  //  --------- GETTERS

  get steps(): Array<ISignUpStep> {
    return this.stateS.steps$.value;
  }

  constructor(
    private stateS: SignUpStateService,
    private authFacadeS: AuthFacadeService,
    private authHttpS: AuthHttpService,
    private formS: SignUpFormHelperService,
    private localStorageS: LocalStorageService,
    private navS: NavigationService,
  ) {
    if (!environment.production) {
      // this.interval = setInterval(() => {
      //   console.log('rootForm', this.formS.rootForm);
      // }, 5000);
    }

    this.watchWizardBtnsState();
  }

  public destroySubscriptions(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------- HELPERS

  private isSmsWasSentAtLeastOneTime(): boolean {
    return this.stateS.isSmsWasSentAtLeastOneTime;
  }

  setPhoneId(id: string): void {
    this.stateS.phoneId$.next(id);
    this.stateS.disablePhone$.next(true);
  }

  public selectIsPhoneDisabled(): Observable<boolean> {
    return this.stateS.disablePhone$.asObservable();
  }

  public selectIsEmailCheckLoading(): Observable<boolean> {
    return this.stateS.isEmailCheckLoading$.asObservable();
  }

  public selectIsPhoneCheckLoading(): Observable<boolean> {
    return this.stateS.isPhoneCheckLoading$.asObservable();
  }

  public isUserFilledFirstStep(): boolean {
    return this.stateS.isUserFilledFirstStep;
  }

  public isUserRegistered(): boolean {
    return this.stateS.isUserRegistered;
  }

  private getCurrStep(): ISignUpStep {
    return this.steps.find(i => this.stateS.currStepIndex$.value === i.index);
  }

  selectPhoneCodeExpireAt(): Observable<dayjs.Dayjs | null> {
    return this.stateS.codeExpireAt$.asObservable();
  }

  private getNextStep(): ISignUpStep {
    return this.steps.find(
      i => this.stateS.currStepIndex$.value + 1 === i.index,
    );
  }

  public getStepByType(type: INVESTOR_SIGN_UP_STEPS): ISignUpStep {
    return this.steps.find(i => i.type === type);
  }

  // ------------- NAVIGATION

  private handleMarketing(currStep: ISignUpStep): void {
    if (!currStep.isMarketingEmailSent && environment.allowRegMarketingEmails) {
      currStep.isMarketingEmailSent = true;
      this.authHttpS
        .signUpMarketing(
          currStep.type,
          this.formS.createStepSignUpPayload(currStep.type),
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
  }

  public setNextStep(): void {
    const currStep = this.getCurrStep();
    const nextStep = this.getNextStep();
    this.stateS.isUserFilledFirstStep = true;
    this.handleMarketing(currStep);
    currStep.isPassed = true;
    const goToNextStep = (): void => {
      nextStep.isVisited = true;
      this.stateS.currStepIndex$.next(nextStep.index);
      this.scrollToTop();
      this.saveToLocalStorage();
    };
    if (nextStep.type === INVESTOR_SIGN_UP_STEPS.ADDRESS) {
      // console.log(this.formS.isPhoneConfirmed , this.isSmsWasSentAtLeastOneTime(), 
    // 'requireSendSms');
      const requireSendSms =
        !this.formS.isPhoneConfirmed && !this.isSmsWasSentAtLeastOneTime();
      // const requireSendSms = true; 
      nextStep.type === INVESTOR_SIGN_UP_STEPS.ADDRESS;
      if (requireSendSms) {
        this.sendCodeToPhone().subscribe(r => {
          this.stateS.phoneId$.next(r.phoneId);
          goToNextStep();
        });
      } else {
        goToNextStep();
      }
    } else {
      goToNextStep();
    }
  }

  public setPrevStep(): void {
    this.stateS.currStepIndex$.next(this.stateS.currStepIndex$.value - 1);
    this.scrollToTop();
    this.saveToLocalStorage();
  }
  public setStepIndex(v: number): void {
    this.stateS.currStepIndex$.next(v);
    this.saveToLocalStorage();
  }

  // ------------ STEPS STATE

  public selectSteps(): Observable<Array<ISignUpStep>> {
    return this.stateS.steps$.asObservable();
  }

  public selectBtnsState(): Observable<ISignUpWizardBtnsState> {
    return this.stateS.btnsState$.asObservable();
  }

  public watchWizardBtnsState(): void {
    combineLatest([
      this.selectSteps().pipe(startWith([])),
      this.selectCurrStepIndex().pipe(startWith(undefined)),
      this.formS.rootForm.valueChanges.pipe(startWith({})),
      this.formS.rootForm.statusChanges.pipe(startWith(null)),
    ])
      .pipe(
        filter(([steps, currIndex]) => {
          return steps.length && typeof currIndex === 'number';
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.updateBtnsState();
      });
  }

  private updateBtnsState(): void {
    const steps: Array<ISignUpStep> = this.stateS.steps$.value;
    const currIndex: number = this.stateS.currStepIndex$.value;
    const currStep = steps.find(s => s.index === currIndex);
    console.log('currStep', currStep);
    const state: ISignUpWizardBtnsState = {
      isLastStep: currIndex === steps.length - 1,
      isFirstStep: currIndex === 0,
      isPrevAvailable: currIndex !== 0,
      isNextAvailable: currIndex !== steps.length - 1,
      isFinishAvailable: currIndex === steps.length - 1,
      isNextBtnDisabled: currIndex === 2 ? false : currStep.form.invalid,
      isFinishDisabled: steps.filter(s => s.form.valid).length !== steps.length,
      activeStep: steps.find(s => s.index === currIndex).type,
      activeStepObj: steps.find(s => s.index === currIndex),
      invalidSteps: steps.filter(i => i.form.invalid).map(i => i.type),
    };
    this.stateS.btnsState$.next(state);
  }

  public selectCurrStepConfig(): Observable<ISignUpStep> {
    return combineLatest([
      this.stateS.steps$.asObservable(),
      this.selectCurrStepIndex(),
    ]).pipe(
      filter(([steps, index]) => steps?.length && typeof index === 'number'),
      map(([steps, index]) => steps.find(i => i.index === index)),
    );
  }

  public selectCurrStepIndex(): Observable<number> {
    return this.stateS.currStepIndex$.asObservable();
  }

  selectIsRegisterLoading(): Observable<boolean> {
    return this.stateS.isRegisterLoading$.asObservable();
  }

  selectIsSendCodeLoading(): Observable<boolean> {
    return this.stateS.isSendSmsLoading$.asObservable();
  }

  selectIsNextLoading(): Observable<boolean> {
    return combineLatest([
      this.stateS.isSendSmsLoading$.asObservable(),
      this.stateS.isConfirmCodeLoading$.asObservable(),
    ]).pipe(map(([sendSms, confirm]) => sendSms || confirm));
  }

  private scrollToTop(): void {
    window.scrollTo({
      top: 0,
    });
  }

  private getAttachedFile(): File {
    const formValue = this.formS.rootForm
      .get(INVESTOR_SIGN_UP_STEPS.DOCS)
      .get(SIGN_UP_FIELDS.DOCUMENTS_LIST).value as ISignUpDocListFormValue;
    return Object.values(formValue).map(i => i.document)[0].file;
  }

  // ----------- PHONE CODE

  public sendCodeToPhone(): Observable<{ phoneId: string }> {
    this.stateS.isSendSmsLoading$.next(true);
    const phone = this.formS.getFormControl(
      INVESTOR_SIGN_UP_STEPS.START,
      SIGN_UP_FIELDS.PHONE,
    ).value;
    const phoneCountry = this.formS.getFormControl(
      INVESTOR_SIGN_UP_STEPS.START,
      SIGN_UP_FIELDS.PHONE_COUNTRY,
    ).value;
    this.formS
      .getFormControl(
        INVESTOR_SIGN_UP_STEPS.ADDRESS,
        SIGN_UP_FIELDS.PHONE_CONFIRMED,
      )
      .setValue(false);
    return this.authHttpS
      .sendSecureCode({
        phone,
        phoneCountry,
      })
      .pipe(
        catchError(err => {
          return throwError(() => {
            this.stateS.isSendSmsLoading$.next(false);
            return err;
          });
        }),
        tap(() => {
          this.initExpireInUiLabel();
          this.stateS.isSmsWasSentAtLeastOneTime = true;
          this.eventSendCodeToMobileSuccess$.next();
          this.stateS.isSendSmsLoading$.next(false);
        }),
      );
  }

  public initExpireInUiLabel(): void {
    const now = new Date().getTime();
    const expireAt = dayjs(now).add(this.stateS.validDuringSec, 'seconds');
    this.clearTooltipWatchers();
    this.stateS.codeExpireAt$.next(expireAt);
    this.stateS.tooltipTimeout = setTimeout(() => {
      this.stateS.codeExpireAt$.next(null);
      if (!this.formS.isPhoneConfirmed) {
        this.handleExpireWithUnconfirmedCode$.next();
      }
    }, this.stateS.validDuringSec * 1000);
  }
  private clearTooltipWatchers(): void {
    if (this.stateS.tooltipTimeout) {
      clearTimeout(this.stateS.tooltipTimeout);
      this.stateS.tooltipTimeout = null;
    }
  }

  // --------------- FINISH API

  finish(): void {
    this.stateS.isRegisterLoading$.next(true);
    const payload = this.formS.createSignUpPayload();
    payload.phoneId = this.stateS.phoneId$.value;
    this.authFacadeS.register(payload, this.getAttachedFile()).subscribe({
      next: data => {
        this.localStorageS.remove(LOCAL_STORAGE_KEYS.SIGN_UP_DATA);
        this.authFacadeS.setAuthUser(data.user);
        this.authFacadeS.setJwtTokens(data.tokens);
        this.stateS.isUserRegistered = true;
        this.stateS.isRegisterLoading$.next(false);
        setTimeout(() => {
          this.navS.goToRoute(IB_ROUTES.EMAIL_CONFIRMATION);
        }, 10);
      },
      error: () => {
        this.stateS.isRegisterLoading$.next(false);
      },
    });
  }

  // ----------- DEF STATE + LOCAL STORAGE

  public createDefState(): void {
    const rootForm = this.stateS.rootForm$.value;
    const steps = this.createDefSteps();
    steps.forEach(i => rootForm.addControl(i.type, i.form));
    this.formS.createFormCtrlChangeListeners();
    this.stateS.steps$.next(steps);
    this.updateBtnsState();
  }

  initDefStepsData(): void {
    const storedData = this.localStorageS.get(
      LOCAL_STORAGE_KEYS.SIGN_UP_DATA,
    ) as ISignUpLocalStorage;
    if (storedData) {
      // show forms first of all
      const steps = storedData.steps.map(i => {
        i.form = this.formS.createStepForm(i.type);
        return i;
      });
      steps.forEach(i => this.formS.rootForm.addControl(i.type, i.form));
      (
        Object.keys(storedData.formRawData) as Array<INVESTOR_SIGN_UP_STEPS>
      )?.forEach(stepName => {
        const stepFg = this.formS.rootForm.get(stepName) as FormGroup;
        if (stepFg) {
          Object.entries(storedData.formRawData[stepName]).forEach(d => {
            const formCtrlName = d[0];
            const val = d[1];
            stepFg?.get(formCtrlName)?.setValue(val);
          });
        }
      });
      this.formS.initWhatForByAccType();
      this.formS.initSavingForOtherByAccType();
      this.formS.initIdentificationNumberType();
      this.formS.rootForm.updateValueAndValidity();
      steps.forEach(step => {
        if (step.isVisited || step.isPassed) {
          step.form.markAllAsTouched();
        }
      });
      this.formS.createFormCtrlChangeListeners();

      this.stateS.currStepIndex$.next(storedData.currStepIndex);
      this.stateS.steps$.next(steps);
    } else {
      this.createDefState();
    }
    this.updateBtnsState();
    this.initSaveToLocalStorage();
  }

  public clearAllSteps(): void {
    this.setStepIndex(0);
    this.steps.forEach(i => (i.isVisited = false));
    this.formS.rootForm.reset();
  }

  private initSaveToLocalStorage(): void {
    this.formS.rootForm.valueChanges
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(() => {
        this.saveToLocalStorage();
      });
  }

  private saveToLocalStorage(): void {
    const formData = this.formS.rootForm.getRawValue();
    formData[INVESTOR_SIGN_UP_STEPS.START][SIGN_UP_FIELDS.PHONE] = '';
    formData[INVESTOR_SIGN_UP_STEPS.START][SIGN_UP_FIELDS.PHONE_COUNTRY] = '';
    formData[INVESTOR_SIGN_UP_STEPS.START][SIGN_UP_FIELDS.PASSWORD] = '';
    formData[INVESTOR_SIGN_UP_STEPS.START][SIGN_UP_FIELDS.PASSWORD_REPEAT] = '';
    delete formData[INVESTOR_SIGN_UP_STEPS.ADDRESS][
      SIGN_UP_FIELDS.PHONE_CONFIRMED
    ];
    formData[INVESTOR_SIGN_UP_STEPS.DOCS][SIGN_UP_FIELDS.DOCUMENTS_LIST] = {};
    const signUpData: ISignUpLocalStorage = {
      steps: this.steps.map(i => {
        return {
          ...i,
          form: undefined,
        };
      }),
      formRawData: formData,
      currStepIndex: this.stateS.currStepIndex$.value,
    };
    this.localStorageS.set(LOCAL_STORAGE_KEYS.SIGN_UP_DATA, signUpData);
  }

  private createDefSteps(): Array<ISignUpStep> {
    return [
      {
        title: 'page.sign-up.steps.0.title',
        titleCompany: 'page.sign-up.steps.0.title',
        index: 0,
        description: 'page.sign-up.steps.0.descr',
        icon: 'question_answer',
        isVisited: true,
        type: INVESTOR_SIGN_UP_STEPS.OBJECTIVE,
        form: null,
      },
      {
        title: 'page.sign-up.steps.1.title',
        titleCompany: 'page.sign-up.steps.1.title-company',
        index: 1,
        description: 'page.sign-up.steps.1.descr',
        descriptionCompany: 'page.sign-up.steps.1.descr-company',
        icon: 'account_circle',
        isVisited: false,
        type: INVESTOR_SIGN_UP_STEPS.START,
        form: null,
      },
      {
        title: 'page.sign-up.steps.2.title',
        titleCompany: 'page.sign-up.steps.2.title',
        index: 2,
        description: 'page.sign-up.steps.2.descr',
        descriptionCompany: 'page.sign-up.steps.2.descr-company',
        icon: 'place',
        type: INVESTOR_SIGN_UP_STEPS.ADDRESS,
        form: null,
      },
      {
        title: 'page.sign-up.steps.3.title',
        titleCompany: 'page.sign-up.steps.3.title',
        index: 3,
        description: 'page.sign-up.steps.3.descr',
        icon: 'security',
        type: INVESTOR_SIGN_UP_STEPS.DOCS,
        form: null,
      },
      {
        title: 'page.sign-up.steps.4.title',
        titleCompany: 'page.sign-up.steps.4.title',
        index: 4,
        description: 'page.sign-up.steps.4.descr',
        icon: 'account_balance',
        type: INVESTOR_SIGN_UP_STEPS.FINISH,
        form: null,
      },
    ].map(i => {
      i.form = this.formS.createStepForm(i.type);
      return i;
    });
  }
}
